// import axios from 'axios';
import { RootStore } from './rootStore';
import { action, computed, decorate, observable } from 'mobx';
import { ManagerDto, ReviewAdminDto, ReviewEmployeeAccountDto, ReviewEmployeeDto, FeedbackDto, RolePermissionInfo, QarFormData, QarFormManagerData } from './models/qarFormModels';
import { ReviewEmployeesResponse, ReviewManagerDto } from './models/qarFormModels';
import { EmployeeReviewActions } from './models/enums';
import QARFormHelper from 'src/pages/QARForm/helper';
import api from 'src/utils/api';

export class QARFormStore {

    walkthrough: boolean = false;

    quarters: ReviewAdminDto[] | undefined;
    employees: ReviewEmployeeDto[] | undefined;
    employeesChanges: ReviewEmployeeDto[] | undefined;
    qarFormData: QarFormData[] | undefined;
    qarFormManagerData: QarFormManagerData[] | undefined;

    delegationsInProgress: {[employeeId: string]: ManagerDto} = {};

    delegatedUnmatchedAccounts: ReviewEmployeeDto[] | undefined;
    delegatedUnmatchedAccountsChanges: ReviewEmployeeDto[] | undefined;

    manager: ReviewManagerDto | undefined;

    allManagers: ManagerDto[] | undefined;

    selectedQuarterId: string | undefined;
    selectedManagerId: string | undefined;

    rolePermissionInfo: RolePermissionInfo | undefined;

    bulkReviewChecked: boolean = false;
    bulkReviewIndeterminate: boolean = false;

    unmatchedBulkReviewChecked: boolean = false;
    unmatchedBulkReviewIndeterminate: boolean = false;

    private rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.qarFormData = [];
        this.qarFormManagerData = [];
        //makeAutoObservable(this)
    }
    
    async saveQarData(quarterId: string, managerId: string) {
      
        const qarFormDataItem: QarFormData = {
            quarterId: quarterId,
            managerId: managerId,
            delegatedUnmatchedAccounts: this.delegatedUnmatchedAccounts!,
            delegatedUnmatchedAccountsChanges: this.delegatedUnmatchedAccountsChanges!,
            reviewingManager: this.manager!,
            employees: this.employees!,
            employeesChanges: this.employeesChanges!
        };
        
        this.qarFormData?.push(qarFormDataItem);

        this.manager = undefined;
        this.employees = undefined;
        this.employeesChanges = undefined;

        this.delegatedUnmatchedAccounts = undefined;
        this.delegatedUnmatchedAccountsChanges = undefined;

        this.bulkReviewChecked = false;
        this.bulkReviewIndeterminate = false;

        this.unmatchedBulkReviewChecked = false;
        this.unmatchedBulkReviewIndeterminate = false;
    }

    async saveChangedQarManagerData(managerId: string) {
        
        const qarFormManagerData: QarFormManagerData = {
            managerId: managerId,
            employees: this.employees!,
            employeesChanges: this.employeesChanges!,
            delegatedUnmatchedAccounts: this.delegatedUnmatchedAccounts!,
            delegatedUnmatchedAccountsChanges: this.delegatedUnmatchedAccountsChanges!
        }

        this.qarFormManagerData?.push(qarFormManagerData);

            this.employees = undefined;
            this.employeesChanges = undefined;
            this.delegatedUnmatchedAccounts = undefined;
            this.delegatedUnmatchedAccountsChanges = undefined;

            this.bulkReviewChecked = false;
            this.bulkReviewIndeterminate = false;
            this.unmatchedBulkReviewChecked = false;
            this.unmatchedBulkReviewIndeterminate = false;
    }

    async loadCurrentQarDataForm(quarterId: string, managerId: string) {
        
        const filteredByIds = this.qarFormData?.find(x => x.quarterId === quarterId && x.managerId === managerId); 
        if (filteredByIds) {
            
            this.manager = filteredByIds?.reviewingManager;
            this.employees = filteredByIds?.employees;
            this.employeesChanges = filteredByIds?.employeesChanges;
            this.delegatedUnmatchedAccounts = filteredByIds?.delegatedUnmatchedAccounts;
            this.delegatedUnmatchedAccountsChanges = filteredByIds?.delegatedUnmatchedAccountsChanges;
        }
    }
    
    async loadCurrentQarManagerData(managerId: string) {

        const filteredByManagerId = this.qarFormManagerData?.find(x => x.managerId === managerId);

        if (filteredByManagerId) {
            this.employees = filteredByManagerId?.employees;
            this.employeesChanges = filteredByManagerId?.employeesChanges;
            this.delegatedUnmatchedAccounts = filteredByManagerId?.delegatedUnmatchedAccounts;
            this.delegatedUnmatchedAccountsChanges = filteredByManagerId?.delegatedUnmatchedAccountsChanges;
        }
    }

    async loadFormData(managerId: string, reviewAdminId: string | null ) {

        this.manager = undefined;
        this.employees = undefined;
        this.employeesChanges = undefined;

        this.delegatedUnmatchedAccounts = undefined;
        this.delegatedUnmatchedAccountsChanges = undefined;

        this.bulkReviewChecked = false;
        this.bulkReviewIndeterminate = false;

        this.unmatchedBulkReviewChecked = false;
        this.unmatchedBulkReviewIndeterminate = false;

        this.selectedManagerId = undefined;
        // this.selectedQuarter = undefined;

        if (this.qarFormData?.some(x => x.quarterId === this.selectedQuarterId
            && x.managerId === this.rootStore.userStore.currentUserID)) {

            await this.loadCurrentQarDataForm(this.selectedQuarterId!, this.rootStore.userStore.currentUserID);
        }
        else {

            const response = await api.get<ReviewEmployeesResponse>('QARForm/GetFormData', {
                params: {
                    managerId: managerId,
                    reviewAdminId: reviewAdminId
                }
            });

            if (!api.responseOk(response)) {
                return;
            }

            // assign all initial store values here
            // this will trigger rerendering of components
            // that depend on these properties
            this.quarters = response.data.quarters;
            this.manager = response.data.reviewingManager;
            this.employees = response.data.employees;
            this.employeesChanges = response.data.employees;

            this.delegatedUnmatchedAccounts = response.data.delegatedUnmatchedAccounts;
            this.delegatedUnmatchedAccountsChanges = response.data.delegatedUnmatchedAccounts;
        }

        if(this.manager == null) {
            // indicates that there is no quarter
            // reviews for this manager
            return;
        }
        
        this.selectedManagerId = this.manager.id;

        if (!reviewAdminId) {
            this.selectedQuarterId = this.quarters![this.quarters!.length - 1].managerReviewAdminId;
        } else {
            this.selectedQuarterId = this.quarters!.find(x => x.managerReviewAdminId === reviewAdminId)!.managerReviewAdminId
        }

        // trade secret
        // setTimeout(() => {
        //     this.employees = response.data.employees;                
        //     this.employeesChanges = response.data.employees;                
        // }, 400)
    }

    async loadEmployees(managerId: string) {

        this.employees = undefined;
        this.employeesChanges = undefined;

        this.bulkReviewChecked = false;
        this.bulkReviewIndeterminate = false;

        this.unmatchedBulkReviewChecked = false;
        this.unmatchedBulkReviewIndeterminate = false;

              const responseEmployees = await api.get<ReviewEmployeeDto[]>('QARForm/GetEmployees', {
                  params: {
                      managerId: managerId,
                      reviewAdminId: this.selectedQuarterId
                  }
              });
  
              if (!api.responseOk(responseEmployees)) {
                  return;
              }
  
              // assign relevant store values here
              // this will trigger rerendering of components
              // that depend on these properties
              this.employees = responseEmployees.data;
              this.employeesChanges = responseEmployees.data;
              this.selectedManagerId = managerId;
    }

    async loadRolePermissionInfo(roleId: string) {

        this.rolePermissionInfo = undefined;

        const responseRolePermissionInfo = await api.get<RolePermissionInfo>('pg/GetPGsByRoleId', {
            params: {
                roleId: roleId,
            }
        });
        
        if(!api.responseOk(responseRolePermissionInfo)) {
            return;
        }

        // assign relevant store values here
        // this will trigger rerendering of components
        // that depend on these properties
        this.rolePermissionInfo = responseRolePermissionInfo.data;

    }

    async loadDelegatedUnmatchedAccounts(managerId: string) {

        this.delegatedUnmatchedAccounts = undefined;
        this.delegatedUnmatchedAccountsChanges = undefined;

              const responseDelegatedUnmatcedAccounts = await api.get<ReviewEmployeeDto[]>('QARForm/GetDelegatedUnmatchedAccounts', {
                  params: {
                      managerId: managerId,
                      reviewAdminId: this.selectedQuarterId
                  }
              });
  
              if (!api.responseOk(responseDelegatedUnmatcedAccounts)) {
                  return;
              }
  
              // assign relevant store values here
              // this will trigger rerendering of components
              // that depend on these properties
              this.delegatedUnmatchedAccounts = responseDelegatedUnmatcedAccounts.data;
              this.delegatedUnmatchedAccountsChanges = responseDelegatedUnmatcedAccounts.data;
              this.selectedManagerId = managerId;
    }

    async loadAllManagers() {

        this.allManagers = undefined;

        const response = await api.get('QARForm/GetDelegationManagers');

        if(!api.responseOk(response)) {
            return;
        }

        this.allManagers = response.data;
    }

    async saveReview() {

        // saving more than 50 accounts
        let inBulkReview = this.shouldDisplayBulkReview;

        this.delegatedUnmatchedAccounts = undefined;
        this.employees = undefined;        

        const data = {
            initiatorId: this.manager!.id,
            reviewerId: this.selectedManagerId,
            employeeReviews: this.employeesChanges!.flatMap(x => x.accounts),
            delegatedUnmatchedAccountReviews: this.delegatedUnmatchedAccountsChanges!.flatMap(x => x.accounts)
        };

        let response = undefined;
        if(inBulkReview) {
            response = await api.post('QARForm/SaveReview', data, {
                timeout: 3E5 // wait 5 minutes, go drink coffee
            });
        } else {
            response = await api.post('QARForm/SaveReview', data);
        }

        if(!api.responseOk(response)) {            
            return response;
        }

        await this.loadEmployees(this.selectedManagerId!)
        await this.loadDelegatedUnmatchedAccounts(this.selectedManagerId!)
        return response;
    }

    async saveFeedback(data: FeedbackDto) {
        const response = await api.post('QARForm/SaveFeedback', data);
        if (!api.responseOk(response)) {
            this.rootStore.uiStateStore.pushMessage("Your feedback was submitted, thanks!", "success");
        }
    }

    get selectedQuarter() {
        return this.quarters?.find(x => x.managerReviewAdminId === this.selectedQuarterId)
    }

    get selectedManager() {
        if (this.manager?.id === this.selectedManagerId) {
            return this.manager;
        }

        return this.manager?.myManagers?.find(x => x.id === this.selectedManagerId)
    }
    
    get accountChanges() {

        const currentAccountChanges : ReviewEmployeeAccountDto[] = []

        if (this.employees && this.employeesChanges) {
            for (let i = 0; i < this.employees.length; i++) {
                const accounts = this.employees[i].accounts;
                const accountsChanges = this.employeesChanges[i].accounts;

                for (let j = 0; j < accounts.length; j++) {
                    const action = accounts[j].action;
                    const actionChanges = accountsChanges[j].action;

                    if (action.name !== actionChanges.name) {
                        currentAccountChanges.push(accountsChanges[j])
                    }
                }
            }
        }

        return currentAccountChanges;
    }

    get unmatchedAccountChanges() {

        const currentAccountChanges : ReviewEmployeeAccountDto[] = []

        if (this.delegatedUnmatchedAccounts && this.delegatedUnmatchedAccountsChanges) {
            for (let i = 0; i < this.delegatedUnmatchedAccounts.length; i++) {
                const accounts = this.delegatedUnmatchedAccounts[i].accounts;
                const accountsChanges = this.delegatedUnmatchedAccountsChanges[i].accounts;

                for (let j = 0; j < accounts.length; j++) {
                    const action = accounts[j].action;
                    const actionChanges = accountsChanges[j].action;

                    if (action.name !== actionChanges.name) {
                        currentAccountChanges.push(accountsChanges[j])
                    }
                }
            }
        }

        return currentAccountChanges;
    }

    get hasChanges() {

        if (this.employees && this.employeesChanges) {
            for (let i = 0; i < this.employees.length; i++) {
                const accounts = this.employees[i].accounts;
                const accountsChanges = this.employeesChanges[i].accounts;

                for (let j = 0; j < accounts.length; j++) {
                    const action = accounts[j].action;
                    const actionChanges = accountsChanges[j].action;

                    if (action.name !== actionChanges.name 
                        || action.comment !== actionChanges.comment 
                        || action.ticketId !== actionChanges.ticketId) {
                        return true;
                    }                    
                }
            }
        }

        if (this.delegatedUnmatchedAccounts && this.delegatedUnmatchedAccountsChanges) {
            for (let i = 0; i < this.delegatedUnmatchedAccounts.length; i++) {
                const accounts = this.delegatedUnmatchedAccounts[i].accounts;
                const accountsChanges = this.delegatedUnmatchedAccountsChanges[i].accounts;

                for (let j = 0; j < accounts.length; j++) {
                    const action = accounts[j].action;
                    const actionChanges = accountsChanges[j].action;

                    if (action.name !== actionChanges.name) {
                        return true;
                    }
                }
            }
        }

        return false;
    }

    get isCompleted() {

        if (!this.employees || !this.delegatedUnmatchedAccounts) {
            return false;
        }

        for (let i = 0; i < this.employees.length; i++) {
            const accounts = this.employees[i].accounts;

            for (let j = 0; j < accounts.length; j++) {
                const action = accounts[j].action;

                if (action.name === EmployeeReviewActions.MissingAction) {
                    return false;
                }
            }
        }

        for (let i = 0; i < this.delegatedUnmatchedAccounts.length; i++) {
            const accounts = this.delegatedUnmatchedAccounts[i].accounts;

            for (let j = 0; j < accounts.length; j++) {
                const action = accounts[j].action;

                if (action.name === EmployeeReviewActions.MissingAction) {
                    return false;
                }
            }
        }

        return true;
    }

    get willComplete() {

        if(this.isCompleted) {
            return false;            
        }

        if (!this.employeesChanges || !this.delegatedUnmatchedAccountsChanges) {
            return false;
        }

        for (let i = 0; i < this.employeesChanges.length; i++) {
            const accounts = this.employeesChanges[i].accounts;

            for (let j = 0; j < accounts.length; j++) {
                const action = accounts[j].action;

                if (action.name === EmployeeReviewActions.MissingAction) {
                    return false;
                }
            }
        }

        for (let i = 0; i < this.delegatedUnmatchedAccountsChanges.length; i++) {
            const accounts = this.delegatedUnmatchedAccountsChanges[i].accounts;

            for (let j = 0; j < accounts.length; j++) {
                const action = accounts[j].action;

                if (action.name === EmployeeReviewActions.MissingAction) {
                    return false;
                }
            }
        }

        return true;
    }

    get employeesWithError() {
        return  this.employeesChanges?.filter(e => e.accounts.some(a => a.action.valid === false));
    }

    get delegatedUnmatchedAccountsWithError() {
        return  this.delegatedUnmatchedAccountsChanges?.filter(e => e.accounts.some(a => a.action.valid === false));
    }

    get shouldDisplayBulkReview(){
        if (!this.selectedQuarter || this.selectedQuarter?.isManagerReviewAdminCompleted) return false;

        let accountReviews = 0;
        if (this.employees){
            accountReviews += this.employees!.flatMap(x => x.accounts).length;
        }

        if (this.delegatedUnmatchedAccounts){
            accountReviews += this.delegatedUnmatchedAccounts!.flatMap(x => x.accounts).length;
        }

        if(accountReviews < 50) return false;

        return true;
    }

    setBulkReviewsIndeterminate = () => {
        if(this.bulkReviewChecked) {
            let accounts = this.employees!.flatMap(x => x.accounts);
            let accountChanges = this.employeesChanges!.flatMap(x => x.accounts);
    
            // all accounts checked, indeterminate should be false
            if(QARFormHelper.masterBulkReviewChecked(accounts, accountChanges)) {
                this.bulkReviewIndeterminate = false;
            } else {
                this.bulkReviewIndeterminate = true;
            }
        }

        if(this.unmatchedBulkReviewChecked) {
            let accounts = this.delegatedUnmatchedAccounts!.flatMap(x => x.accounts);
            let accountChanges = this.delegatedUnmatchedAccountsChanges!.flatMap(x => x.accounts);
    
            // all accounts checked, indeterminate should be false            
            if(QARFormHelper.masterBulkReviewChecked(accounts, accountChanges)) {
                this.unmatchedBulkReviewIndeterminate = false;
            } else {
                this.unmatchedBulkReviewIndeterminate = true;
            }
        }  
    }    
}

decorate(QARFormStore, {
    walkthrough: observable,

    loadFormData: action,
    loadEmployees: action,
    saveReview: action,
    saveQarData: action,
    saveChangedQarManagerData: action,
    loadCurrentQarDataForm: action,
    loadCurrentQarManagerData: action,

    selectedQuarterId: observable,
    selectedQuarter: computed,
    selectedManagerId: observable,
    selectedManager: computed,

    quarters: observable,
    manager: observable,

    employees: observable,
    employeesChanges: observable,
    qarFormData: observable,
    qarFormManagerData: observable,

    bulkReviewChecked: observable,
    bulkReviewIndeterminate: observable,    
    unmatchedBulkReviewChecked: observable,
    unmatchedBulkReviewIndeterminate: observable,
    setBulkReviewsIndeterminate: action,

    rolePermissionInfo: observable,

    delegatedUnmatchedAccounts: observable,
    delegatedUnmatchedAccountsChanges: observable,

    allManagers: observable,
    delegationsInProgress: observable,

    hasChanges: computed,
    accountChanges: computed,
    unmatchedAccountChanges: computed,
    isCompleted: computed,
    willComplete: computed,
    employeesWithError: computed,
    delegatedUnmatchedAccountsWithError: computed,
    shouldDisplayBulkReview: computed
})